import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Phone, Globe, MapPin } from "lucide-react";
import { SCHOOL } from "@/lib/school";

// Demo quick-fill accounts are ONLY shown when explicitly enabled (preview/dev).
// In production this flag is unset, so no demo credentials ship to the live site.
const SHOW_DEMOS = process.env.REACT_APP_SHOW_DEMO_LOGINS === "true";

const DEMOS: { role: "admin" | "teacher" | "student" | "parent" | "cashier" | "examhead"; email: string; password: string; color: string }[] = SHOW_DEMOS ? [
  { role: "admin",    email: "admin@lfs.edu",    password: "admin123",    color: "bg-indigo-500" },
  { role: "cashier",  email: "cashier@lfs.edu",  password: "cashier123",  color: "bg-fuchsia-500" },
  { role: "examhead", email: "examhead@lfs.edu", password: "examhead123", color: "bg-purple-500" },
  { role: "teacher",  email: "teacher@lfs.edu",  password: "teacher123",  color: "bg-cyan-500" },
  { role: "student",  email: "student@lfs.edu",  password: "student123",  color: "bg-emerald-500" },
  { role: "parent",   email: "parent@lfs.edu",   password: "parent123",   color: "bg-amber-500" },
] : [];

export function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation() as { state?: { from?: { pathname?: string } } };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // If already logged in, bounce to dashboard
  useEffect(() => {
    if (user) {
      navigate(loc.state?.from?.pathname || "/dashboard", { replace: true });
    }
  }, [user, navigate, loc.state?.from?.pathname]);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email || !password) {
      toast.warning("Enter email and password");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate(loc.state?.from?.pathname || "/dashboard", { replace: true });
    } catch (e: any) {
      toast.error(e?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-100 p-4">
      <div className="grid w-full max-w-5xl gap-8 lg:grid-cols-2">
        {/* Branding side */}
        <div className="hidden flex-col justify-between p-8 lg:flex">
          <div className="flex items-center gap-4">
            <img src={SCHOOL.logo} alt={SCHOOL.name} className="h-16 w-16 rounded-xl bg-white object-contain shadow-md" />
            <div>
              <div className="text-2xl font-bold leading-tight text-emerald-700">{SCHOOL.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{SCHOOL.tagline}</div>
            </div>
          </div>
          <div>
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-slate-900">
              One ERP for everyone at <span className="text-emerald-700">{SCHOOL.name}.</span>
            </h1>
            <p className="mt-4 max-w-md text-slate-600">
              Manage students, fees, attendance, exams, staff, and parent communication —
              all from a single, secure portal. Mobile apps available for parents and teachers.
            </p>
            <div className="mt-6 space-y-1.5 text-xs text-slate-500">
              <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {SCHOOL.address}</div>
              <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {SCHOOL.phone}</div>
              <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> {SCHOOL.website}</div>
            </div>
          </div>
          <div className="flex gap-2 text-xs text-muted-foreground">
            <span>© {new Date().getFullYear()} {SCHOOL.name}</span>
          </div>
        </div>

        {/* Login card */}
        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle>Sign in to your account</CardTitle>
            <CardDescription>Use your school email to access the admin portal.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="you@lfs.edu" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in</> : "Sign in"}
              </Button>
            </form>

            {DEMOS.length > 0 && (
              <div className="mt-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Demo accounts</div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {DEMOS.map((d) => (
                    <button
                      key={d.role}
                      type="button"
                      data-testid={`demo-login-${d.role}`}
                      onClick={() => { setEmail(d.email); setPassword(d.password); }}
                      className="group flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-xs hover:bg-accent"
                    >
                      <span className={`h-2 w-2 rounded-full ${d.color}`} />
                      <span className="font-semibold capitalize">{d.role}</span>
                      <span className="truncate text-muted-foreground">{d.email}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
